/**
 * THE CLIENT HALF OF C-18 — a deliberate port of `infra/src/data-access/idempotency-key.ts`.
 * =========================================================================================
 *
 * This is a second implementation of a function that already exists server-side, which is normally
 * the wrong shape. It is here for one reason: the browser cannot import from the CDK package, and
 * the alternative — generating a key and *hoping* the server's canonicalisation is a no-op on it —
 * is how the "validated the trimmed value, then sent the raw one" defect family works.
 *
 * The duplication is held honest by a test rather than by this comment:
 * `test/idempotency-canonicalisation.test.ts` imports the REAL
 * `infra/src/data-access/idempotency-key.ts` and asserts the two functions agree, transformation
 * for transformation, over a corpus that includes NFC-decomposed input, U+00A0 and U+FEFF edges,
 * mixed case, and the Turkish dotted capital I (U+0130) whose `toLowerCase` CHANGES LENGTH. If
 * anyone edits either side, that test fails.
 *
 * ORDER IS LOAD-BEARING AND IT IS THE SERVER'S ORDER: **NFC → trim → case-fold**, and the fold is
 * `toLowerCase`, NEVER `toLocaleLowerCase` — under a Turkish locale the locale-aware version folds
 * `I` to `ı`, so the same key would canonicalise differently depending on the device's language
 * settings. An idempotency key whose meaning depends on the phone's locale is not an idempotency
 * key, and a field technician's phone locale is not something we control.
 *
 * WHAT THE CLIENT ACTUALLY EMITS. `newIdempotencyKey()` returns a lowercase UUIDv4, which is
 * already canonical and already inside `CANONICAL_CLIENT_KEY_PATTERN` — so in the normal path
 * canonicalisation is a no-op and the server echoes back exactly what we sent. The canonicaliser is
 * still applied at the point of generation, because "it happens to be canonical" is a property of
 * today's generator and the check costs nothing.
 *
 * ⚠️ THE KEY IS GENERATED **ONCE, AT ENQUEUE**, AND NEVER REGENERATED ON RETRY. That rule lives in
 * `outbox.ts` (the key is a stored field of the persisted row, not a value computed at send time),
 * and `test/sync-retry.test.ts` asserts it by capturing the key off two consecutive failed
 * attempts. A key regenerated per attempt turns every retry into a new submission, which is the
 * duplicate-billing defect C-18 exists to prevent, wearing a different hat.
 */

/** Mirrors the server's `CANONICAL_CLIENT_KEY_PATTERN`. Lowercase-only, by construction. */
export const CANONICAL_CLIENT_KEY_PATTERN = /^[a-z0-9_.:-]{1,120}$/;

/** Mirrors the server's `CANONICAL_CLIENT_KEY_MAX_LENGTH`. */
export const CANONICAL_CLIENT_KEY_MAX_LENGTH = 120;

/** Mirrors the server's `KeyCanonicalisation` union, name for name. */
export type KeyCanonicalisation = 'nfc-normalised' | 'whitespace-trimmed' | 'case-folded';

export interface CanonicalClientKey {
  readonly key: string;
  readonly applied: readonly KeyCanonicalisation[];
  readonly wasCanonical: boolean;
}

/**
 * Canonicalise the client half of an idempotency key. Byte-for-byte the server's algorithm.
 *
 * Idempotent: `canonicalise(canonicalise(x)).key === canonicalise(x).key`, asserted as a property
 * in the test file.
 */
export function canonicaliseClientIdempotencyKey(clientKey: string): CanonicalClientKey {
  const applied: KeyCanonicalisation[] = [];

  const normalised = clientKey.normalize('NFC');
  if (normalised !== clientKey) {
    applied.push('nfc-normalised');
  }

  const trimmed = normalised.trim();
  if (trimmed !== normalised) {
    applied.push('whitespace-trimmed');
  }

  // `toLowerCase`, never `toLocaleLowerCase`. See the header.
  const folded = trimmed.toLowerCase();
  if (folded !== trimmed) {
    applied.push('case-folded');
  }

  return { key: folded, applied, wasCanonical: applied.length === 0 };
}

/** Mirrors the server's `isCanonicalClientIdempotencyKey`. */
export function isCanonicalClientIdempotencyKey(clientKey: string): boolean {
  if (!CANONICAL_CLIENT_KEY_PATTERN.test(clientKey)) {
    return false;
  }
  return canonicaliseClientIdempotencyKey(clientKey).wasCanonical;
}

/**
 * A fresh client idempotency key: a lowercase UUIDv4.
 *
 * A UUID is case-insensitive **by specification** (RFC 4122 §3 makes lowercase canonical), which is
 * what makes the server's case-folding strictly correct for our keys rather than a lossy
 * convergence — see the base64url cost documented in the server file.
 *
 * `crypto.randomUUID` needs a secure context, which the PWA always has (HTTPS, or `localhost` in
 * dev). The fallback is not defensive padding: iOS Safari exposed `crypto.getRandomValues` for
 * years before `randomUUID`, and an old iPad in a van is exactly this app's audience. The fallback
 * uses the same CSPRNG and sets the version/variant bits by hand — it is a real v4, not a
 * `Math.random` stand-in, because a guessable idempotency key is a cross-tenant overwrite attempt
 * waiting for a namespacing bug.
 */
export function newIdempotencyKey(): string {
  const raw = randomUuid();
  const canonical = canonicaliseClientIdempotencyKey(raw);
  if (!isCanonicalClientIdempotencyKey(canonical.key)) {
    // Unreachable: a v4 UUID is 36 chars of `[0-9a-f-]`. Thrown rather than silently returned,
    // because a key the server will refuse must not reach the outbox — a queued item with an
    // unusable key can never drain, and it would look like a network problem forever.
    throw new Error('generated idempotency key is not canonical');
  }
  return canonical.key;
}

function randomUuid(): string {
  const cryptoObj: Crypto | undefined = globalThis.crypto;
  if (cryptoObj !== undefined && typeof cryptoObj.randomUUID === 'function') {
    return cryptoObj.randomUUID().toLowerCase();
  }
  if (cryptoObj === undefined || typeof cryptoObj.getRandomValues !== 'function') {
    throw new Error('no CSPRNG available: refusing to generate an idempotency key');
  }
  const bytes = cryptoObj.getRandomValues(new Uint8Array(16));
  // Version 4, variant 10xx. Indices are fixed by RFC 4122 §4.4.
  bytes[6] = ((bytes[6] ?? 0) & 0x0f) | 0x40;
  bytes[8] = ((bytes[8] ?? 0) & 0x3f) | 0x80;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0'));
  return [
    hex.slice(0, 4).join(''),
    hex.slice(4, 6).join(''),
    hex.slice(6, 8).join(''),
    hex.slice(8, 10).join(''),
    hex.slice(10, 16).join(''),
  ].join('-');
}
